import React, { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { SignedIn, SignedOut, RedirectToSignIn, SignIn, SignUp } from '@clerk/clerk-react'
import { ApiProvider } from './contexts/ApiContext'
import { AppNav } from './components/AppNav'
import { SetupNudge } from './components/SetupNudge'
import { VarianceAlertBanner } from './components/VarianceAlertBanner'
import { Spinner } from './components/ui/Spinner'
import { PageErrorBoundary } from './components/PageErrorBoundary'

const SchedulePage           = lazy(() => import('./pages/SchedulePage'))
const ChannelManagerPage     = lazy(() => import('./pages/messaging/ChannelManagerPage'))
const BroadcastComposerPage  = lazy(() => import('./pages/messaging/BroadcastComposerPage'))
const HandoffNoteHistoryPage = lazy(() => import('./pages/messaging/HandoffNoteHistoryPage'))
const FiscalSetupPage        = lazy(() => import('./pages/FiscalSetupPage'))
const ChartOfAccountsPage    = lazy(() => import('./pages/ChartOfAccountsPage'))
const JournalEntriesPage     = lazy(() => import('./pages/accounting/JournalEntriesPage'))
const TrialBalancePage       = lazy(() => import('./pages/accounting/TrialBalancePage'))
const PeriodClosePage        = lazy(() => import('./pages/accounting/PeriodClosePage'))
const PayrollSettingsPage    = lazy(() => import('./pages/accounting/PayrollSettingsPage'))
const StaffMealsPage         = lazy(() => import('./pages/accounting/StaffMealsPage'))
const VendorsPage            = lazy(() => import('./pages/inventory/VendorsPage'))
const ProductsPage           = lazy(() => import('./pages/inventory/ProductsPage'))
const ReceivingHistoryPage   = lazy(() => import('./pages/inventory/ReceivingHistoryPage'))
const CountsPage             = lazy(() => import('./pages/inventory/CountsPage'))
const TransfersPage          = lazy(() => import('./pages/inventory/TransfersPage'))
const CountAuditPage         = lazy(() => import('./pages/inventory/CountAuditPage'))
const LedgerWastePage        = lazy(() => import('./pages/inventory/LedgerWastePage'))
const SalesImportsPage       = lazy(() => import('./pages/inventory/SalesImportsPage'))
const WastePage              = lazy(() => import('./pages/inventory/WastePage'))
const DailyCountSetupPage    = lazy(() => import('./pages/inventory/DailyCountSetupPage'))
const RolesPage              = lazy(() => import('./pages/hr/RolesPage'))
const StaffPage              = lazy(() => import('./pages/hr/StaffPage'))
const EmployeePage           = lazy(() => import('./pages/hr/EmployeePage'))
const RoleSetupPage          = lazy(() => import('./pages/hr/RoleSetupPage'))
const MenuItemsPage          = lazy(() => import('./pages/menu/MenuItemsPage'))
const MenuRecipesPage        = lazy(() => import('./pages/recipes/MenuRecipesPage'))
const PrepItemsPage          = lazy(() => import('./pages/recipes/PrepItemsPage'))
const RecipeEditorPage       = lazy(() => import('./pages/recipes/RecipeEditorPage'))
const PricingTargetsPage     = lazy(() => import('./pages/recipes/PricingTargetsPage'))
const OverTargetPage         = lazy(() => import('./pages/recipes/OverTargetPage'))
const MenuEngineeringPage    = lazy(() => import('./pages/recipes/MenuEngineeringPage'))
const QFactorPage            = lazy(() => import('./pages/recipes/QFactorPage'))
const ActualVsTheoreticalPage = lazy(() => import('./pages/recipes/ActualVsTheoreticalPage'))
const PushedReportPage       = lazy(() => import('./pages/recipes/PushedReportPage')) // rb-10c-1b: any employee
const ProductionPage         = lazy(() => import('./pages/recipes/ProductionPage')) // Recipe Builder rb-7
const PrepPlanPage           = lazy(() => import('./pages/recipes/PrepPlanPage')) // Recipe Builder rb-9
const PosTerminalPage        = lazy(() => import('./pages/pos/PosTerminalPage'))
const KdsPage                = lazy(() => import('./pages/pos/KdsPage'))
const CashTipsPage           = lazy(() => import('./pages/pos/CashTipsPage'))
const TipPoolConfigPage      = lazy(() => import('./pages/pos/TipPoolConfigPage'))
const PrintersPage           = lazy(() => import('./pages/pos/PrintersPage'))
const PosSettingsPage        = lazy(() => import('./pages/pos/PosSettingsPage')) // Recipe Builder rb-6
const CardPaymentsPage       = lazy(() => import('./pages/pos/CardPaymentsPage'))
const LaborVsSalesPage       = lazy(() => import('./pages/labor/LaborVsSalesPage'))
const CostVsSalesPage        = lazy(() => import('./pages/accounting/CostVsSalesPage'))
const GuestOrderPage         = lazy(() => import('./pages/guest/GuestOrderPage'))
const ReservationsPage       = lazy(() => import('./pages/reservations/ReservationsPage'))
const HoursOfOperationPage   = lazy(() => import('./pages/hours/HoursOfOperationPage'))
const SrlItemsPage           = lazy(() => import('./pages/srl/SrlItemsPage'))
const SrlCountsPage          = lazy(() => import('./pages/srl/SrlCountsPage'))
const CertificationsPage     = lazy(() => import('./pages/training/CertificationsPage'))
const CompliancePage         = lazy(() => import('./pages/training/CompliancePage'))
const OnboardingPage         = lazy(() => import('./pages/training/OnboardingPage'))
const HrRecordsPage          = lazy(() => import('./pages/training/HrRecordsPage'))
const TrainingLibraryPage    = lazy(() => import('./pages/training/TrainingLibraryPage'))
const MyTrainingPage         = lazy(() => import('./pages/training/MyTrainingPage'))
const PublicBookingPage      = lazy(() => import('./pages/booking/PublicBookingPage'))
const CustomersPage          = lazy(() => import('./pages/crm/CustomersPage'))
const CustomerProfilePage    = lazy(() => import('./pages/crm/CustomerProfilePage'))
const ReportsHubPage         = lazy(() => import('./pages/reports/ReportsHubPage'))
const SalesAnalyticsPage     = lazy(() => import('./pages/reports/SalesAnalyticsPage'))
const InventoryLossPage      = lazy(() => import('./pages/reports/InventoryLossPage'))
const PrimeCostPage          = lazy(() => import('./pages/reports/PrimeCostPage'))
const CashPage               = lazy(() => import('./pages/cash/CashPage'))
const DailyBriefPage         = lazy(() => import('./pages/gm/DailyBriefPage'))
const OrgChartPage           = lazy(() => import('./pages/org/OrgChartPage'))
const OrgCanvasPage          = lazy(() => import('./pages/org/OrgCanvasPage'))
const CalendarPage           = lazy(() => import('./pages/calendar/CalendarPage'))
const PolicyListPage         = lazy(() => import('./pages/policy/PolicyListPage'))
const PolicyDetailPage       = lazy(() => import('./pages/policy/PolicyDetailPage'))
const SetupWizardPage        = lazy(() => import('./pages/setup/SetupWizardPage'))
const YourDataPage           = lazy(() => import('./pages/account/YourDataPage')) // DEF-207b-2

function PageFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '0.75rem', color: '#64748b' }}>
      <Spinner size={28} />
      <span>Loading…</span>
    </div>
  )
}

function AppShell() {
  // DEF-216: a screen that fails to draw shows "did not load" + Reload inside the shell, never a white page; keyed by
  // the path so moving to another screen clears it.
  const { pathname } = useLocation()
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppNav />
      <main style={{ flex: 1, overflowY: 'auto', background: 'var(--color-bg)' }}>
        <SetupNudge />
        <VarianceAlertBanner />
        <PageErrorBoundary key={pathname}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/"                          element={<Navigate to="/schedule" replace />} />
            <Route path="/schedule"                  element={<SchedulePage />} />
            <Route path="/labor/report"              element={<LaborVsSalesPage />} />
            <Route path="/accounting/cost-vs-sales"     element={<CostVsSalesPage />} />
            <Route path="/messaging/channels"        element={<ChannelManagerPage />} />
            <Route path="/messaging/broadcast"       element={<BroadcastComposerPage />} />
            <Route path="/messaging/handoff-notes"   element={<HandoffNoteHistoryPage />} />
            <Route path="/accounting/setup"             element={<FiscalSetupPage />} />
            <Route path="/accounting/accounts"          element={<ChartOfAccountsPage />} />
            <Route path="/accounting/journal-entries"   element={<JournalEntriesPage />} />
            <Route path="/accounting/trial-balance"     element={<TrialBalancePage />} />
            <Route path="/accounting/period-close"      element={<PeriodClosePage />} />
            <Route path="/accounting/payroll-settings"  element={<PayrollSettingsPage />} />
            <Route path="/accounting/staff-meals"       element={<StaffMealsPage />} />
            <Route path="/reports"                      element={<ReportsHubPage />} />
            <Route path="/reports/sales"                element={<SalesAnalyticsPage />} />
            <Route path="/reports/inventory"            element={<InventoryLossPage />} />
            <Route path="/reports/prime-cost"           element={<PrimeCostPage />} />
            <Route path="/gm/daily-brief"               element={<DailyBriefPage />} />
            <Route path="/org-chart"                    element={<OrgChartPage />} />
            <Route path="/org-canvas"                   element={<OrgCanvasPage />} />
            <Route path="/your-data"                    element={<YourDataPage />} />
            <Route path="/calendar"                     element={<CalendarPage />} />
            <Route path="/policies"                     element={<PolicyListPage />} />
            <Route path="/policies/:id"                 element={<PolicyDetailPage />} />
            <Route path="/inventory/vendors"            element={<VendorsPage />} />
            <Route path="/inventory/products"           element={<ProductsPage />} />
            <Route path="/inventory/receivings"         element={<ReceivingHistoryPage />} />
            <Route path="/inventory/counts"             element={<CountsPage />} />
            <Route path="/inventory/transfers"          element={<TransfersPage />} />
            <Route path="/inventory/count-audit"        element={<CountAuditPage />} />
            <Route path="/inventory/ledger-waste"       element={<LedgerWastePage />} />
            <Route path="/inventory/sales-imports"      element={<SalesImportsPage />} />
            <Route path="/inventory/waste"              element={<WastePage />} />
            <Route path="/inventory/daily-count"        element={<DailyCountSetupPage />} />
            <Route path="/hr/staff"                     element={<StaffPage />} />
            <Route path="/hr/staff/:id"                 element={<EmployeePage />} />
            <Route path="/hr/roles"                     element={<RolesPage />} />
            <Route path="/hr/role-setup"                element={<RoleSetupPage />} />
            <Route path="/menu/items"                   element={<MenuItemsPage />} />
            <Route path="/recipes/menu"                 element={<MenuRecipesPage />} />
            <Route path="/recipes/prep"                 element={<PrepItemsPage />} />
            <Route path="/recipes/menu-items/:id"       element={<RecipeEditorPage kind="menu" />} />
            <Route path="/recipes/prep-items/:id"       element={<RecipeEditorPage kind="prep" />} />
            <Route path="/recipes/pricing"              element={<PricingTargetsPage />} />
            <Route path="/recipes/over-target"          element={<OverTargetPage />} />
            <Route path="/recipes/menu-engineering"     element={<MenuEngineeringPage />} />
            <Route path="/recipes/q-factor"             element={<QFactorPage />} />
            <Route path="/recipes/actual-vs-theoretical" element={<ActualVsTheoreticalPage />} />
            <Route path="/pushed-reports/:id"         element={<PushedReportPage />} />
            <Route path="/recipes/production"           element={<ProductionPage />} />
            <Route path="/recipes/prep-plan"            element={<PrepPlanPage />} />
            <Route path="/pos/terminal"                 element={<PosTerminalPage />} />
            <Route path="/pos/kds"                      element={<KdsPage />} />
            <Route path="/pos/cash-tips"                element={<CashTipsPage />} />
            <Route path="/pos/tip-pool"                 element={<TipPoolConfigPage />} />
            <Route path="/pos/printers"                 element={<PrintersPage />} />
            <Route path="/pos/settings"                 element={<PosSettingsPage />} />
            <Route path="/pos/payments"                 element={<CardPaymentsPage />} />
            <Route path="/hours-of-operation"          element={<HoursOfOperationPage />} />
            <Route path="/reservations"                element={<ReservationsPage />} />
            <Route path="/customers"                   element={<CustomersPage />} />
            <Route path="/customers/:id"               element={<CustomerProfilePage />} />
            <Route path="/cash"                        element={<CashPage />} />
            <Route path="/srl/items"                   element={<SrlItemsPage />} />
            <Route path="/srl/counts"                  element={<SrlCountsPage />} />
            <Route path="/training/certifications"     element={<CertificationsPage />} />
            <Route path="/training/compliance"         element={<CompliancePage />} />
            <Route path="/training/onboarding"         element={<OnboardingPage />} />
            <Route path="/training/records"            element={<HrRecordsPage />} />
            <Route path="/training/library"            element={<TrainingLibraryPage />} />
            <Route path="/my-training"                 element={<MyTrainingPage />} />
            <Route path="/setup"                       element={<SetupWizardPage />} />
            <Route path="*"                             element={<Navigate to="/schedule" replace />} />
          </Routes>
        </Suspense>
        </PageErrorBoundary>
      </main>
    </div>
  )
}

function CenteredAuth({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
      }}
    >
      {children}
    </div>
  )
}

// The authenticated app, with the same redirect-when-signed-out gate as before —
// but it now redirects to the local /sign-in route (set via ClerkProvider
// signInUrl) instead of Clerk's hosted Account Portal.
function GatedApp() {
  return (
    <>
      <SignedIn>
        <ApiProvider>
          <AppShell />
        </ApiProvider>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  )
}

export default function App() {
  return (
    <Routes>
      {/* Public, embedded auth pages (outside the signed-in gate) */}
      <Route
        path="/sign-in/*"
        element={
          <CenteredAuth>
            <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" />
          </CenteredAuth>
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <CenteredAuth>
            <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />
          </CenteredAuth>
        }
      />
      {/* Public guest QR ordering surface (OFF-3 G2) — outside the signed-in gate */}
      <Route
        path="/order/:slug"
        element={
          <Suspense fallback={<PageFallback />}>
            <GuestOrderPage />
          </Suspense>
        }
      />
      {/* Public online booking surface (Module 5 inc-3) — outside the signed-in gate */}
      <Route
        path="/book/:slug"
        element={
          <Suspense fallback={<PageFallback />}>
            <PublicBookingPage />
          </Suspense>
        }
      />
      {/* Everything else requires auth */}
      <Route path="/*" element={<GatedApp />} />
    </Routes>
  )
}
